import { memo, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Button, IconButton, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UseFormReturn, Controller } from 'react-hook-form';

type TOptions = { label: string; id: string }[];

function MultipleOptions({
  form, // recibiendo el formulario como prop
  id,
  value,
  isEditable = true,
  isReadOnly,
  optionsCreated,
  itemFull,
  onChangeInputs
}: {
  id: string;
  form: UseFormReturn;
  value?: string;
  isEditable?: boolean;
  isReadOnly?: boolean;
  onChangeInputs: (id: string, key: 'options', value?: TOptions) => void;
  optionsCreated: TOptions;
  itemFull?: { idSurveyQuestion: string; isRequired: boolean };
}) {
  const { t } = useTranslation('global');
  const [options, setOptions] = useState<TOptions>([{ label: '', id: '1' }]);
  const [selectedValue, setSelectedValue] = useState('');

  const handleInputChange = (key: string, newValue: string) => {
    setOptions((prevOptions) =>
      prevOptions.map((option) =>
        option?.id === key ? { ...option, label: newValue } : option
      )
    );
  };

  const handleAddOption = () => {
    const nextId = options.length
      ? (parseInt(options[options.length - 1].id, 10) + 1).toString()
      : '1';
    setOptions((prevOptions) => [...prevOptions, { label: '', id: nextId }]);
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleDeleteOption = (key: string) => {
    if (options.length === 1) return;

    const newOptions = options.filter((option) => option?.id !== key);
    const updatedOptions = newOptions.map((option, index) => ({
      ...option,
      id: (index + 1).toString()
    }));
    setOptions(updatedOptions);
  };

  useMemo(
    () => onChangeInputs && onChangeInputs(id, 'options', options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, options]
  );

  useMemo(
    () => optionsCreated?.length && setOptions(optionsCreated),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, optionsCreated]
  );

  return (
    <Grid spacing={2} container>
      {isEditable ? (
        <Stack
          alignItems="stretch"
          display="flex"
          flexDirection="column"
          width="100%"
        >
          <Grid
            display="flex"
            justifyContent="end"
            marginBottom={1}
            marginTop={1}
            width="100%"
          >
            <Button
              onClick={handleAddOption}
              sx={{ width: '10rem' }}
              variant="outlined"
            >
              + {t('survey.template.addOption')}
            </Button>
          </Grid>
          {options?.map((option) => (
            <div
              key={option.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                marginTop: '1rem'
              }}
            >
              <Radio
                checked={selectedValue === option.label}
                onChange={handleRadioChange}
                value={option.label}
                disabled
              />
              <TextField
                onChange={({ target }) =>
                  handleInputChange(option?.id, target?.value)
                }
                placeholder={t('survey.template.enterOption')}
                sx={{ marginRight: 1, width: '80%' }}
                value={option?.label}
                variant="outlined"
              />
              {options?.length > 1 && (
                <IconButton onClick={() => handleDeleteOption(option.id)}>
                  -
                </IconButton>
              )}
            </div>
          ))}
        </Stack>
      ) : (
        <div>
          {/* Campo oculto para idSurveyQuestion */}
          <Controller
            control={form.control}
            defaultValue={itemFull?.idSurveyQuestion || ''}
            name={`${id}-idSurveyQuestion`} // Registra idSurveyQuestion en el formulario
            render={({ field }) => (
              <input
                type="hidden"
                {...field}
                value={itemFull?.idSurveyQuestion}
              />
            )}
          />

          <Controller
            control={form.control} // Usamos el control de react-hook-form
            defaultValue={value || ''} // valor por defecto del radio
            name={`${id}-select`} // nombre del campo que debe coincidir con el id del formulario
            render={({ field, fieldState: { error } }) => (
              <RadioGroup
                {...field}
                name="radio-buttons-group"
                value={value || (form.getValues(`${id}-select`) as string)}
              >
                {optionsCreated?.map((option) => (
                  <FormControlLabel
                    control={<Radio disabled={isReadOnly} />}
                    key={option?.id}
                    label={option?.label}
                    value={option?.id}
                  />
                ))}
                {error && (
                  <Typography color="error" variant="caption">
                    {t('common.commonMessages.requiredField', {
                      field: `${id}-select`
                    })}
                  </Typography>
                )}
              </RadioGroup>
            )}
            rules={{
              required: {
                value: isReadOnly ? false : itemFull?.isRequired,
                message: t('common.commonMessages.requiredField', {
                  field: `${id}-select`
                })
              }
            }}
          />
        </div>
      )}
    </Grid>
  );
}

export default memo(MultipleOptions);
